import { createSelector } from 'reselect';
import type { FontFamily, FontStyle } from 'packages/types/fonts';
import type {
    FontActivationToggle,
    FontDeletion,
    FontList,
    FontListQuery,
    FontManagementState,
    PendingFontFile,
    ReplacementOverwrite,
} from './types';

const NO_FAMILIES: FontFamily[] = [];
const NO_FONT_IDS: number[] = [];

export const fontManagement = (state: any): FontManagementState => state.fontManagement;

export const fontList = (state: any): FontList => fontManagement(state).list;

export const fontListQuery = (state: { fontManagement: FontManagementState }): FontListQuery =>
    state.fontManagement.query;

export const isEditorFontsStale = (state: any): boolean => fontManagement(state).isEditorFontsStale;

export const defaultFontChange = (state: any): { fontId: number; isSaving: boolean } | undefined => {
    const change = fontManagement(state).defaultChange;

    return change && { fontId: change.fontId, isSaving: change.status === 'saving' };
};

export const fontFamilies = (state: any): FontFamily[] => fontList(state).loaded?.families ?? NO_FAMILIES;

export const fontFamilyByName =
    (name: string) =>
    (state: any): FontFamily | undefined =>
        fontFamilies(state).find((family) => family.name === name);

export const selectedFontIds = (state: any): number[] => fontList(state).loaded?.selectedFontIds ?? NO_FONT_IDS;

export const isDefaultFontSelected = (state: any): boolean => {
    const selected = selectedFontIds(state);

    return fontFamilies(state).some((family) =>
        family.fonts.some((font) => font.isDefault && selected.includes(font.id)),
    );
};

const replacementState = (state: { fontManagement: FontManagementState }) => state.fontManagement.replacement;

export const fontReplacement = createSelector(
    [replacementState],
    (replacement): { font: FontStyle; isReplacing: boolean; overwrite: ReplacementOverwrite | undefined } | undefined =>
        replacement && {
            font: replacement.font,
            isReplacing: replacement.status === 'replacing',
            overwrite: replacement.overwrite,
        },
);

export const replacementCandidates = createSelector([replacementState], (replacement): FontFamily[] =>
    replacement?.candidates.status === 'ready'
        ? replacement.candidates.families.flatMap((family) => {
              const fonts = family.fonts.filter((font) => font.isActive && font.id !== replacement.font.id);

              return fonts.length ? [{ ...family, fonts }] : [];
          })
        : NO_FAMILIES,
);

export const areReplacementCandidatesLoading = (state: { fontManagement: FontManagementState }): boolean =>
    replacementState(state)?.candidates.status === 'loading';

export const fontDeletion = (state: any): FontDeletion | undefined => fontManagement(state).deletion;

export const fontActivationToggle = (state: any): FontActivationToggle | undefined =>
    fontManagement(state).toggleActivation;

export const isUploading = (state: any): boolean => fontManagement(state).upload !== undefined;

const duplicatesOf = new WeakMap<PendingFontFile[], PendingFontFile[]>();

export const replaceRequest = (state: any): PendingFontFile[] | undefined => {
    const { upload } = fontManagement(state);

    if (upload?.status !== 'confirming replace') {
        return undefined;
    }

    if (!duplicatesOf.has(upload.files)) {
        duplicatesOf.set(
            upload.files,
            upload.files.filter(({ exists }) => exists),
        );
    }

    return duplicatesOf.get(upload.files);
};
